import { parseApiOrIsoDate } from "./apiDates";

/** Máximo de unidades asociadas que puede llevar el formulario de transporte. */
export const MAX_ASSOCIATED_VEHICLES = 2;

/**
 * Forma del vehículo asociado dentro de react-hook-form.
 *
 * La API de vehículos y los eventos usan nombres distintos (`plate`,
 * `insurancePolicy`, `technicalReviewExpirationDate`, ...). Centralizar la
 * conversión evita que elegir un vehículo existente y los prefills de egreso
 * hidraten conjuntos de campos diferentes.
 */
export type TrailerVehicleFormValues = {
  id?: string;
  vehiclePlate: string;
  vehicleTypeId: string;
  vehicleBrand: string;
  vehicleColor: string;
  insuranceCompany: string;
  policyNumber: string;
  insuranceExpirationDate: Date | null;
  appliesVtv: boolean;
  vtvExpirationDate: Date | null;
  insurancePhoto: File | string | null;
  vtvPhoto: File | string | null;
};

type VehicleLike = Record<string, any> | null | undefined;

const asId = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && typeof (value as any).id === "string") {
    return (value as any).id;
  }
  return "";
};

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

/**
 * Lleva una ficha de vehículo de la API al shape que usan `trailerVehicles`.
 *
 * `technicalReviewPhoto1` es el nombre histórico de la carga de VTV; se
 * toleran las otras dos variantes que pueden devolver respuestas más nuevas o
 * legacy. El valor final sigue siendo sólo una referencia de imagen (string),
 * nunca se vuelve a subir hasta que el guardia elige un `File` nuevo.
 */
export const toTrailerVehicleFormValues = (
  vehicle: VehicleLike,
  overrides: Partial<TrailerVehicleFormValues> = {}
): TrailerVehicleFormValues => {
  const vehicleId = asId(vehicle?.id);
  const vtvExpirationDate = parseApiOrIsoDate(vehicle?.technicalReviewExpirationDate);

  return {
    ...(vehicleId ? { id: vehicleId } : {}),
    vehiclePlate: asText(vehicle?.plate),
    vehicleTypeId: asId(vehicle?.vehicleType),
    vehicleBrand: asText(vehicle?.brand),
    vehicleColor: asText(vehicle?.color),
    insuranceCompany: asId(vehicle?.insuranceCompanyId),
    policyNumber: asText(vehicle?.insurancePolicy),
    insuranceExpirationDate: parseApiOrIsoDate(vehicle?.insuranceExpirationDate),
    appliesVtv: !!vehicle?.technicalReviewExpirationDate,
    vtvExpirationDate,
    insurancePhoto: vehicle?.insurancePhoto ?? null,
    vtvPhoto:
      vehicle?.vtvPhoto ??
      vehicle?.technicalReviewPhoto1 ??
      vehicle?.technicalReviewPhoto ??
      null,
    ...overrides,
  };
};
